import { AIRLINES } from './data/airlines';
import { AIRPORTS } from './data/airports';

export interface Airport {
  code: string;
  city: string;
  name: string;
  country: string;
}

export interface Airline {
  code: string;
  name: string;
  country: string;
  hasLogo: boolean;
}

/** Turkish-insensitive comparison key: "İstanbul", "istanbul" and "ISTANBUL" match. */
export function fold(text: string): string {
  return text
    .toLocaleLowerCase('tr-TR')
    .replace(
      /[çğıöşüâîû]/g,
      (c) => ({ ç: 'c', ğ: 'g', ı: 'i', ö: 'o', ş: 's', ü: 'u', â: 'a', î: 'i', û: 'u' })[c] ?? c,
    );
}

const airports: Airport[] = AIRPORTS.map(([code, city, name, country]) => ({
  code,
  city,
  name,
  country,
}));
const airlines: Airline[] = AIRLINES.map(([code, name, country, hasLogo]) => ({
  code,
  name,
  country,
  hasLogo,
}));
const airportByCode = new Map(airports.map((a) => [a.code, a]));
const airlineByCode = new Map(airlines.map((a) => [a.code, a]));

export const findAirport = (code: string | null | undefined) =>
  code ? airportByCode.get(code.toUpperCase()) : undefined;
export const findAirline = (code: string | null | undefined) =>
  code ? airlineByCode.get(code.toUpperCase()) : undefined;

/**
 * Airport search for the itinerary fields. An exact code wins ("fra" → Frankfurt
 * first), then city and name prefixes, then anything containing the text; Turkish
 * airports rank first within each tier, as v1 did.
 */
export function searchAirports(query: string, limit = 8): Airport[] {
  const q = fold(query.trim());
  if (!q) return [];
  const scored: { airport: Airport; score: number }[] = [];
  for (const airport of airports) {
    const code = airport.code.toLowerCase();
    const city = fold(airport.city);
    const name = fold(airport.name);
    let score = -1;
    if (code === q) score = 0;
    else if (city.startsWith(q)) score = 1;
    else if (name.startsWith(q) || code.startsWith(q)) score = 2;
    else if (city.includes(q) || name.includes(q)) score = 3;
    if (score >= 0) scored.push({ airport, score: score * 2 + (airport.country === 'TR' ? 0 : 1) });
  }
  return scored
    .sort((a, b) => a.score - b.score)
    .slice(0, limit)
    .map((s) => s.airport);
}

/** Airline search: code first ("pc" → Pegasus), then name. */
export function searchAirlines(query: string, limit = 8): Airline[] {
  const q = fold(query.trim());
  if (!q) return [];
  const scored: { airline: Airline; score: number }[] = [];
  for (const airline of airlines) {
    const code = airline.code.toLowerCase();
    const name = fold(airline.name);
    let score = -1;
    if (code === q) score = 0;
    else if (name.startsWith(q)) score = 1;
    else if (name.includes(q)) score = 2;
    if (score >= 0) scored.push({ airline, score: score * 2 + (airline.country === 'TR' ? 0 : 1) });
  }
  return scored
    .sort((a, b) => a.score - b.score)
    .slice(0, limit)
    .map((s) => s.airline);
}

/**
 * An airport as a place to meet or collect a car: "Frankfurt Havalimanı (FRA)",
 * "İstanbul Havalimanı (IST)". Unknown codes come back as typed.
 */
export function airportPlaceName(code: string): string {
  const a = findAirport(code);
  if (!a) return code.toUpperCase();
  const name = /havaliman/i.test(a.name) ? a.name : `${a.name} Havalimanı`;
  return `${name} (${a.code})`;
}
